package com.vamvisions.main.components

import androidx.compose.runtime.Composable
import com.vamvisions.main.core.presentation.navigation.Route
import com.vamvisions.main.navigation.AppLink
import com.vamvisions.main.navigation.ExternalLink
import com.vamvisions.main.theme.AppStyleSheet
import org.jetbrains.compose.web.attributes.AttrsScope
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLAnchorElement

/**
 * A rounded outline link with an optional [icon] before its [label], opening a page of this website in place. An
 * [iconOnly] pill hides the label and keeps it as the accessible name.
 */
@Composable
fun LinkPill(
    label: String,
    route: Route,
    onNavigate: (Route) -> Unit,
    iconOnly: Boolean = false,
    icon: (@Composable () -> Unit)? = null,
) {
    AppLink(route, onNavigate, attrs = { linkPillAttrs(label, iconOnly) }) { LinkPillContent(label, iconOnly, external = false, icon) }
}

/**
 * A rounded outline link with an optional [icon] before its [label], opening another site in a new tab. The label ends
 * with "↗", which is left out of the accessible name. An [iconOnly] pill hides the label and the arrow and keeps the
 * label as the accessible name.
 */
@Composable
fun LinkPill(label: String, href: String, iconOnly: Boolean = false, icon: (@Composable () -> Unit)? = null) {
    ExternalLink(href, attrs = { linkPillAttrs(label, iconOnly) }) { LinkPillContent(label, iconOnly, external = true, icon) }
}

/** A rounded outline `mailto:` link to [address], showing the address after an optional [icon]. It opens in place. */
@Composable
fun EmailLinkPill(address: String, icon: (@Composable () -> Unit)? = null) {
    A(href = "mailto:$address", attrs = { linkPillAttrs(address, iconOnly = false) }) {
        LinkPillContent(address, iconOnly = false, external = false, icon)
    }
}

private fun AttrsScope<HTMLAnchorElement>.linkPillAttrs(label: String, iconOnly: Boolean) {
    classes(AppStyleSheet.linkPill)
    if (iconOnly) attr("aria-label", label)
}

/** The icon and the arrow are decorative: the label, shown or not, names the link. */
@Composable
private fun LinkPillContent(label: String, iconOnly: Boolean, external: Boolean, icon: (@Composable () -> Unit)?) {
    if (icon != null) Span(attrs = { attr("aria-hidden", "true") }) { icon() }
    if (!iconOnly) {
        Text(label)
        if (external) Span(attrs = { attr("aria-hidden", "true") }) { Text("↗") }
    }
}
